<script>
  import { ethers } from "ethers";
  import { tick } from "svelte";
  import { onMount } from "svelte";
  import WalletInfo from "./WalletInfo.svelte";

  let contractAddress = "";
  let chainId = 1;
  let abi = [];
  let contract;
  let functions = [];
  let provider = null;
  let signer = null;
  let userAddress;
  let balance;
  let isConnected = false;
  let responses = [];
  let userBalance = null;
  let connectedChainId;
  let errorMessage = null;
  let successMessage = null;
  let proxyMessage = null;
  let downloadUrl = null;
  let contractName = null;
  let contractSymbol = null;
  let networks = null;
  let dlfixed = null;
  let chainName = null;
  let network = null;

  function parseToArray(str) {
    // Regex to match Ethereum addresses and add quotes around them
    const regex = /(0x[a-fA-F0-9]{40})/g;
    const jsonString = str.replace(regex, '"$1"');

    try {
      // Parse the modified string as JSON
      return JSON.parse(jsonString);
    } catch (e) {
      console.error("Parsing error:", e);
      return []; // or handle the error as appropriate
    }
  }

  function getQueryStringValue(key) {
    const value = new URLSearchParams(window.location.search).get(key);
    return value;
  }

  onMount(async () => {
    // Fetch network data
    const response = await fetch("https://synabi.skunkr.com/networks");
    networks = await response.json();
    const contractAddressFromQuery = getQueryStringValue("contractAddress");
    const chainIdFromQuery = getQueryStringValue("chainId");

    if (contractAddressFromQuery) {
      contractAddress = contractAddressFromQuery;
    }

    if (chainIdFromQuery) {
      chainId = chainIdFromQuery;
    }

    if (contractAddressFromQuery && chainIdFromQuery) {
      handleSubmit();
    }



    // Set the name property of each network based on the chainId
    for (const [chainId, network] of Object.entries(networks)) {
      switch (chainId) {
        case "1":
          network.name = "Ethereum";
          break;
        case "250":
          network.name = "Fantom";
          break;
        case "8453":
          network.name = "Base Protocol";
          break;
        case "43114":
          network.name = "Avalanche";
          break;
        case "42161":
          network.name = "Arbitrum";
          break;
        case "59144":
          network.name = "Linea";
          break;
        default:
          network.name = `Unknown (${chainId})`;
          break;
      }
    }

    provider = isConnected ? new ethers.providers.Web3Provider(window.ethereum, "any") : new ethers.providers.JsonRpcProvider(networks[chainId].rpcUrls[0]);
  });

  async function connectWallet() {
    if (isConnected) {
      isConnected = false;
      userAddress = null;
      signer = null;
      userBalance = null;
      provider = new ethers.providers.JsonRpcProvider(networks[chainId].rpcUrls[0]);
    } else {
      if (typeof window.ethereum !== "undefined") {
        try {
          // Request access to user's accounts
          const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
          userAddress = accounts[0];

          // Create a Web3Provider instance using window.ethereum
          provider = new ethers.providers.Web3Provider(window.ethereum, "any");

          // Create a Signer instance from the provider
          signer = provider.getSigner();

          // Update the isConnected flag
          isConnected = true;

          network = await provider.getNetwork();
          connectedChainId = network.chainId;
          console.log("BEFORE - Connected network:", network.chainId);
          if (connectedChainId !== chainId) {
            await switchNetwork(chainId);
          }
          await provider.ready
          network = await provider.getNetwork();

          chainName = networks[chainId].name
          // network = await provider.getNetwork();
          //console.log("AFTER - Connected network:", network.chainId);
          //console.log(provider.network)
          // Fetch and update the user's balance
          balance = await provider.getBalance(userAddress);
          userBalance = ethers.utils.formatEther(balance);
          userBalance = parseFloat(userBalance).toFixed(3);
          userBalance += ` ${networks[chainId].nativeToken.symbol}`;
      
        } catch (error) {
          console.error("Failed to connect wallet:", error);

          
        }
      } else {
        alert("Please install MetaMask to use this application!");
      }
    }
  }

  async function handleSubmit() {
    errorMessage = null; // Clear errorMessage
    proxyMessage = null; // Clear proxyMessage
    let rpcUrl;

    try {
      const response = await fetch(
        `https://synabi.skunkr.com/syn_abi/${chainId}/${contractAddress}`,
      );

      // Get the value of the "x-synabi-successrate" header
      const successRate = response.headers.get("x-synabi-successrate");

      abi = await response.json();
      console.log(networks);
      if (successRate) {
        // Parse the numbers from the successRate header value
        const regex = /(\d+) of (\d+) signatures matched/;
        const match = successRate.match(regex);

        if (match) {
          console.log(successRate);

          const matchedCount = parseInt(match[1], 10);
          const totalCount = parseInt(match[2], 10);

          const abiBlob = new Blob([JSON.stringify(abi, null, 2)], {
            type: "application/json",
          });
          downloadUrl = URL.createObjectURL(abiBlob);
          const abiFilename = `abi-${contractAddress}.json`;

          successMessage = `While the ABI was not found, one was synthesized. ${matchedCount} out of ${totalCount} functions were mapped.`;
        } else {
          successMessage = null;
        }
      } else {
        successMessage = null;
      }

      if (isConnected && provider["_network"].chainId !== chainId) {
        try {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: `0x${chainId.toString(16)}` }],
          });
          provider = new ethers.providers.Web3Provider(window.ethereum, "any");
          signer = provider.getSigner();
        } catch (error) {
          console.error("Failed to switch network:", error);
          return;
        }
      } else if (!isConnected) {
        rpcUrl = networks[chainId].rpcUrls[0];
        provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      }

      if (response.status === 403) {
        errorMessage =
          "Sorry, I don't have the ABI for this contract on this chain available.";
        throw new Error(errorMessage);
      }

      // Check if the wallet is connected and if the connected network is different from the target network
      if (isConnected && connectedChainId !== chainId) {
        await switchNetwork(chainId);
      }

      // Initialize the contract with the signer if connected, otherwise with the provider
      contract = isConnected
        ? new ethers.Contract(contractAddress, abi, signer)
        : new ethers.Contract(contractAddress, abi, provider);

      functions = abi.filter((item) => item.type === "function");
      functions.forEach((func) => {
        if (func.inputs) {
          func.inputs.forEach((input) => {
            input.value = "";
          });
        }
      });

      responses = new Array(functions.length).fill(null);
      contractName = null;
      contractSymbol = null;
      if (functions.some((func) => func.name === "name")) {
        console.log("In that bit where it auto-retrieves the name")
        console.log(contract)
        console.log(provider)
        console.log(signer)
        console.log(isConnected)
        console.log(chainId)
        contractName = await contract.name();
      }
      if (functions.some((func) => func.name === "symbol")) {
        contractSymbol = await contract.symbol();
      }

      const proxyAddress = response.headers.get("X-Proxy-For");
      if (proxyAddress) {
        proxyMessage = `This contract is proxying to implementation address ${proxyAddress}`;
      }
    } catch (err) {
      console.error(err);
      errorMessage = err.message;
    }
  }

  async function switchNetwork(targetChainId) {
    console.log('In switchNetwork');
    console.log(`Connected to network with chain ID ${connectedChainId}.`);
    console.log(`Targeting network with chain ID ${targetChainId}.`);

    try {
      const targetNetwork = networks[targetChainId];

      if (!targetNetwork) {
        const errorMessage = `Network with chain ID ${targetChainId} not found.`;
        alert(errorMessage);
        console.error(errorMessage);
        return;
      }

      if (isConnected) {
        const chainIdHex = `0x${targetChainId.toString(16)}`;
        const provider = new ethers.providers.Web3Provider(window.ethereum, "any");
        const network = await provider.getNetwork();

        if (network.chainId !== chainIdHex) {
          try {
            await window.ethereum.request({
              method: "wallet_switchEthereumChain",
              params: [{ chainId: chainIdHex }],
            });
          } catch (error) {
            // Handle the error silently
          }
        }

        const balance = await provider.getBalance(userAddress);
        userBalance = ethers.utils.formatEther(balance);
        userBalance = parseFloat(userBalance).toFixed(3);
        userBalance += ` ${networks[chainId].nativeToken.symbol}`;
        connectedChainId = targetChainId;
      } else {
        provider = new ethers.providers.JsonRpcProvider(targetNetwork.rpcUrls[0]);
      }

      chainName = targetNetwork.name;

      console.log(`Chain name: ${chainName}`);

      chainId = targetChainId;
      

    } catch (err) {
      const errorMessage = `Unable to switch to network with chain ID ${targetChainId}.`;
      console.error(errorMessage, err);
    }
  }
  async function callFunction(funcIndex, func) {
    const inputs = func.inputs;
    contract = isConnected
        ? new ethers.Contract(contractAddress, abi, provider.getSigner())
        : new ethers.Contract(contractAddress, abi, provider);
    const contractFunction = contract[func.name].bind(contract);
    const params = inputs.map((input) => {
      if (input.type.endsWith("[]")) {
        // Parse the string input as an array
        const arrayValue = input.value.slice(1, -1); // Remove square brackets
        return arrayValue.split(",").map((item) => item.trim());
      } else {
        return input.value;
      }
    });

    try {
      if (func.stateMutability === "view" || func.stateMutability === "pure") {
        const result = await contractFunction(...params);
        responses[funcIndex] = result.toString();
      } else {
        if (!isConnected) {
          alert("Please connect your wallet first!");
          return;
        }
        const tx = await contractFunction(...params);
        await tx.wait();
        alert(`Transaction completed: ${tx.hash}`);
      }
    } catch (err) {
      console.error(err);
      if (err.reason) {
        alert(`Error executing function: ${err.reason}`);
      } else {
        alert(`Error executing function: ${err.message}`);
      }
    }
  }

  const contractsToTry = [
    {
      label: "Mambonauts on Avalanche",
      address: "0x2EE475A5CdC31c040ba1af3B0C4D66CA5C31c49a",
      chainId: 43114,
    },
    {
      label: "Stars Arena: Shares on Avalanche",
      address: "0xA481B139a1A654cA19d2074F174f17D7534e8CeC",
      chainId: 43114,
    },
    {
      label: "PaintSwap Finance: Estfor Kingdom Players on Fantom",
      address: "0x058ec56aba13f7fee3ae9c9b91b3bb03bc336143",
      chainId: 250,
    },
    {
      label: "Friendtech: Shares on Base",
      address: "0xCF205808Ed36593aa40a44F10c7f7C2F67d4A4d4",
      chainId: 8453,
    },
  ];

  const chainIdLookup = {
    1: "Ethereum",
    250: "Fantom",
    8453: "Base",
    42161: "Arbitrum",
    43114: "Avalanche",
    59144: "Linea",
  };

  async function onChainRadioButtonChange(event) {
    const selectedChainId = parseInt(event.target.value, 10);
    if (isConnected && chainId !== selectedChainId) {
      await switchNetwork(selectedChainId);
    }
  }

  async function setContract(contract) {
    const targetChainId = contract.chainId;

    if (isConnected && chainId !== targetChainId) {
      await switchNetwork(targetChainId);
    }

    contractAddress = contract.address;
    chainId = targetChainId;
    await handleSubmit();
  }

  $: {
    for (const [id, name] of Object.entries(chainIdLookup)) {
      if (chainId == id) {
        (async () => {
          await tick();
          document.getElementById(name.toLowerCase()).checked = true;
        })();
      }
    }
  }
</script>

<head>
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1, shrink-to-fit=no"
  />
  <link
    rel="stylesheet"
    href="https://maxcdn.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css"
  />
</head>

<div class="container">
  <header class="d-flex justify-content-between align-items-center mt-1 mb-1">
    <div>
      <h1>noabi.help Universal Contract Interface</h1>
      <p>Interact with EVM smart contracts, verified or not...</p>
    </div>
    <!--    <div>
      <a href="https://developers.noabi.help/" target="_blank" rel="noopener"
        >noabi tools for devs</a
      >
    </div>
  -->
  </header>
  <div class="row">
    <div class="col">
      <button class="btn btn-primary" on:click={connectWallet}>
        {#if isConnected}
          Disconnect Wallet
        {:else}
          Connect Wallet
        {/if}
      </button>
    </div>

    <div class="col">
      <WalletInfo {userAddress} {chainName} {userBalance} />
    </div>
  </div>

  <div class="container mt-1">
    <div class="row">
      <div class="col">
        {#if errorMessage}
          <div class="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        {/if}
        {#if successMessage}
          <div class="alert alert-info" role="alert">
            {successMessage}
          </div>
        {/if}
        {#if proxyMessage}
          <div class="alert alert-info" role="alert">
            {proxyMessage}
          </div>
        {/if}
      </div>
    </div>
    <div class="row mt-1">
      <div class="col">
        <h3>Contract Summary</h3>
        {#if abi}
          <p>
            {#if contractName !== null}
              <strong>Name:</strong>
              {contractName ? contractName : "<em>null or empty</em>"}<br />
            {:else}
              <em>Name not available</em><br />
            {/if}
            {#if contractSymbol !== null}
              <strong>Symbol:</strong>
              {contractSymbol ? contractSymbol : "<em>null or empty</em>"}
            {:else}
              <em>Symbol not available</em>
            {/if}
          </p>
        {:else}
          <p>No contract loaded.</p>
        {/if}
      </div>
    </div>

    <div>
      <h5>Contracts to Try:</h5>
      <ul>
        {#each contractsToTry as contract}
          <li>
            <button
              class="btn btn-link p-0"
              on:click={() => setContract(contract)}>{contract.label}</button
            >
          </li>
        {/each}
      </ul>
    </div>
  </div>
  <div class="row">
    <div class="col">
      <form on:submit|preventDefault={handleSubmit}>
        <div class="form-group">
          <input
            class="form-control"
            type="text"
            placeholder="Contract address"
            bind:value={contractAddress}
          />
        </div>
        <style>
          .form-check {
            display: inline-block;
            margin-right: 10px;
          }
        </style>

        <div class="form-group">
          {#each Object.entries(chainIdLookup) as [id, name]}
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                id={name.toLowerCase()}
                name="chain"
                value={id}
                bind:group={chainId}
                on:change={onChainRadioButtonChange}
                checked={chainId == id}
              />
              <label class="form-check-label" for={name.toLowerCase()}>
                {name}
              </label>
            </div>
          {/each}
        </div>

        <button class="btn btn-primary" type="submit">Submit</button>
      </form>
    </div>
  </div>

  {#each functions as func, funcIndex}
    <form
      on:submit|preventDefault={() =>
        callFunction(funcIndex, func, func.inputs)}
      class="card {func.stateMutability === 'view' ||
      func.stateMutability === 'pure'
        ? 'read-only'
        : 'writeable'}"
    >
      <div class="card-body">
        {#if func.inputs.length === 0}
          <h5 class="card-title">{func.name}</h5>
        {:else}
          {#each func.inputs as input, inputIndex}
            <div class="form-group">
              <label for="input-{funcIndex}-{inputIndex}"
                >{func.name} - {input.name} ({input.type})</label
              >
              <input
                id="input-{funcIndex}-{inputIndex}"
                type="text"
                placeholder="{input.name} ({input.type})"
                bind:value={input.value}
                class="form-control"
                on:input={() => {
                  if (input.type === "array") {
                    input.value = parseToArray(input.value);
                  }
                }}
              />
            </div>
          {/each}
        {/if}
        <button
          type="submit"
          class="btn btn-primary"
          disabled={!isConnected &&
            func.stateMutability !== "view" &&
            func.stateMutability !== "pure"}
        >
          {#if func.stateMutability === "view" || func.stateMutability === "pure"}
            Read
          {:else if !isConnected && func.stateMutability !== "view" && func.stateMutability !== "pure"}
            Connect Wallet First
          {:else}
            Write
          {/if}
        </button>
      </div>
    </form>
    {#if responses[funcIndex]}
      <div class="response">Response: {responses[funcIndex]}</div>
    {/if}
  {/each}
</div>
